/**
 * Client for the ops tools that act on ONE shop — the shop file and everything opened from it —
 * plus the operator audit log.
 *
 * Mirrors `api.ts` and `support-api.ts`: the generated `InternalAdminApi` imported directly, an
 * axios instance carrying the operator bearer token, a 401 interceptor that clears the session.
 * A file of its own because those two are already each their domain's client, and the shop file
 * brings a third concern neither has: the per-shop access grant.
 *
 * **Grants.** A read of a shop's data needs the grant `openShopAccess` returns. {@link shopGrant}
 * reads the one stored for a shop; {@link isShopAccessRequired} recognises the API refusing a
 * read for want of one, which the shop file answers by asking for a reason again.
 */

import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';
import { InternalAdminApi } from '../../../../libs/api-client/src/generated/api/internal-admin-api';
import { Configuration } from '../../../../libs/api-client/src/generated/configuration';
import type {
  OperatorAuditEntryDto,
  OperatorAuditListDto,
  ShopAccessGrantDto,
  ShopFileDto,
  ShopSearchResultDto,
} from '../../../../libs/api-client/src/generated/models';
import { clearToken, getToken } from './auth';
import {
  SHOP_ACCESS_REQUIRED,
  forgetShopGrant,
  readShopGrant,
  writeShopGrant,
  type GrantStorage,
} from './shop-file';

// Re-exported from the generated client rather than restated here — see `support-api.ts`.
export type {
  OperatorAuditEntryDto as OperatorAuditEntry,
  OperatorAuditListDto as OperatorAuditList,
  OperatorAuditRowDto as OperatorAuditRow,
  ShopFileAlertDto as ShopFileAlert,
  ShopFileAttentionItemDto as ShopFileAttentionItem,
  ShopFileDocumentDto as ShopFileDocument,
  ShopFileDto as ShopFile,
  ShopFileExportDto as ShopFileExport,
  ShopFileShopifyScopesDto as ShopFileShopifyScopes,
  ShopFileTicketDto as ShopFileTicket,
  ShopFileWebhookDto as ShopFileWebhook,
  ShopSearchHitDto as ShopSearchHit,
} from '../../../../libs/api-client/src/generated/models';

// ── Client ──────────────────────────────────────────────────────────────────

function getApiBasePath(): string {
  const base = import.meta.env['VITE_API_BASE_URL'] ?? '';
  return base ? `${base.replace(/\/$/, '')}/api` : '/api';
}

function createAuthedAxios() {
  const instance = axios.create();
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    return config;
  });
  instance.interceptors.response.use(
    (r: AxiosResponse) => r,
    (err: AxiosError) => {
      if (err.response?.status === 401) {
        clearToken();
        window.location.href = '/login';
      }
      throw err;
    },
  );
  return instance;
}

/** The generated client, authenticated as the signed-in operator. */
export function opsClient(): InternalAdminApi {
  const basePath = getApiBasePath();
  return new InternalAdminApi(
    new Configuration({ basePath }),
    basePath,
    createAuthedAxios(),
  );
}

/** The browser's grant store. A function, so nothing touches `window` at import time. */
function grantStorage(): GrantStorage {
  return window.sessionStorage;
}

// ── Grants ──────────────────────────────────────────────────────────────────

/**
 * The stored grant for a shop, or an empty string when there is none.
 *
 * Empty rather than null so it can be passed straight to a generated method's required
 * `xOpsShopAccess` argument: the API then refuses it with {@link SHOP_ACCESS_REQUIRED}, which is
 * exactly the signal the shop file prompts on.
 */
export function shopGrant(shopId: string): string {
  return readShopGrant(grantStorage(), shopId)?.grant ?? '';
}

/** Whether a failed request was refused for want of a valid grant. Drops the stale grant if so. */
export function isShopAccessRequired(error: unknown, shopId?: string): boolean {
  if (!axios.isAxiosError<{ message?: unknown }>(error)) return false;
  const refused =
    error.response?.status === 403 &&
    error.response.data?.message === SHOP_ACCESS_REQUIRED;
  if (refused && shopId) forgetShopGrant(grantStorage(), shopId);
  return refused;
}

/**
 * The API's error message, or the fallback — without asserting the error's shape. For every ops
 * page to show the server's own validation text rather than "Request failed with status 400".
 */
export function opsErrorMessage(error: unknown, fallback: string): string {
  if (axios.isAxiosError<{ message?: string | string[] }>(error)) {
    const message = error.response?.data?.message;
    if (Array.isArray(message)) return message.join('; ');
    if (message) return message;
  }
  return error instanceof Error ? error.message : fallback;
}

// ── Shop file ───────────────────────────────────────────────────────────────

/** Finds shops by domain, name or email. */
export async function searchShops(q: string): Promise<ShopSearchResultDto> {
  const { data } = await opsClient().searchOpsShops({ q });
  return data;
}

/**
 * Opens a shop with a reason or a ticket, and stores the grant.
 *
 * @param shopIdOrDomain - Shop id, or a myshopify domain (the API resolves it).
 */
export async function openShopAccess(
  shopIdOrDomain: string,
  input: { reason?: string; ticketId?: string },
): Promise<ShopAccessGrantDto> {
  const { data } = await opsClient().openOpsShopAccess({
    shopId: shopIdOrDomain,
    shopAccessRequestDto: input,
  });
  writeShopGrant(grantStorage(), data.shopId, {
    grant: data.grant,
    expiresAt: data.expiresAt,
    auditLogId: data.auditLogId,
    shopDomain: data.shopDomain,
  });
  return data;
}

/** Reads one shop's file with the stored grant. */
export async function fetchShopFile(shopId: string): Promise<ShopFileDto> {
  const { data } = await opsClient().getOpsShopFile({
    shopId,
    xOpsShopAccess: shopGrant(shopId),
  });
  return data;
}

// ── Audit log ───────────────────────────────────────────────────────────────

/** Filters of the audit list. */
export interface AuditLogFilters {
  operator?: string;
  action?: string;
  shopDomain?: string;
  from?: string;
  to?: string;
  page?: number;
  limit?: number;
}

/** The audit list, without payloads. */
export async function fetchAuditLog(
  filters: AuditLogFilters,
): Promise<OperatorAuditListDto> {
  const { data } = await opsClient().listOperatorAudit(filters);
  return data;
}

/** One entry including its payload. The API records that it was opened. */
export async function fetchAuditEntry(
  id: string,
): Promise<OperatorAuditEntryDto> {
  const { data } = await opsClient().getOperatorAuditEntry({ id });
  return data;
}
